import React, { FC, useEffect, useState, useMemo, useRef } from 'react';
import { Flex, useDisclosure } from '@chakra-ui/react';
import { ReportHeader, ReportBody, ReportFooter } from '../index';
import { ReportTypes } from '../../types';
import { AddBlockToReport } from '../AddBlockToReport';
import { ReportBoundary } from './components';
import { useReportCRUD } from '../../hooks/useReportCRUD';
import { ReportBodyRef } from '../ReportBody';

export const ReportContainer: FC<{ reportMeta: ReportTypes.ReportMeta }> =
  React.memo(({ reportMeta }) => {
    const [isEditMode, setEditMode] = useState(false);
    const [reportData, setReportData] = useState(() => reportMeta);
    const [isLocalUpdate, setIsLocalUpdate] = useState(true); // Flag to control API calls
    const [isSaving, setIsSaving] = useState(false);
    const reportFooterRef = useRef(null);
    const reportBodyRef = useRef<ReportBodyRef>(null);

    const showAddBlockDisclosure = useDisclosure();

    useEffect(() => {
      const { layoutData, isLoading } = reportMeta ?? {};
      const { tiles } = layoutData ?? {};
      // Set edit mode based on whether tiles exist and data is loaded
      if (Array.isArray(tiles) && tiles?.length === 0 && !isLoading) {
        setEditMode(true);
      }
      setReportData(reportMeta);
    }, [reportMeta]);

    useEffect(() => {
      // when edit mode is changed, re-render the whole data, to reset the data
      if (isEditMode) {
        setReportData(reportMeta);
      }
    }, [isEditMode, reportMeta]);

    const { layoutData: report, type, isLoading, error } = reportData ?? {};

    // Memoize CRUD config to prevent unnecessary hook re-initialization
    const crudConfig = useMemo(
      () => ({
        reportData,
        reportId: report?.id || '',
        setReportData,
        setHasUnsavedChanges: (hasChanges: boolean) => {
          reportFooterRef.current?.setHasUnsavedChanges(hasChanges);
        },
        setEditMode,
        setIsSaving,
        setIsLocalUpdate,
      }),
      [
        reportData,
        report?.id,
        setReportData,
        setEditMode,
        setIsSaving,
        setIsLocalUpdate,
      ]
    );

    // Initialize simple CRUD operations
    const {
      handleAddBlocks,
      onTileMetaChange,
      handleRemoveBlock,
      handleLayoutChange,
      handleSaveChanges,
      handleCancelChanges,
      syncFooterState,
      clearFilterChanges,
      getCurrentMergedData,
      layoutResetKey,
      onFilterChange,
    } = useReportCRUD({
      ...crudConfig,
      onBlocksAdded: (newBlockPositions: any[]) => {
        // Scroll to new blocks after a delay to ensure DOM is updated
        setTimeout(() => {
          if (newBlockPositions && newBlockPositions.length > 0) {
            reportBodyRef.current?.scrollToNewBlocks(newBlockPositions);
          }
        }, 200);
      },
    });

    // Reset footer unsaved changes when entering edit mode
    useEffect(() => {
      if (isEditMode) {
        // Clear filter changes when entering edit mode to start with clean slate
        clearFilterChanges();
        // Use a small delay to ensure the footer ref is available
        setTimeout(() => {
          syncFooterState();
        }, 0);
      }
    }, [isEditMode, syncFooterState, clearFilterChanges]);

    if (!report || type === 'invalid' || error || isLoading) {
      return <ReportBoundary reportData={reportData} />;
    }

    const hasOriginalTiles = reportMeta?.layoutData?.tiles?.length > 0;

    return (
      <Flex w="100%" h="100%" direction="column" data-report-container>
        <ReportHeader
          reportData={reportData}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          onFilterChange={onFilterChange}
          hasOriginalTiles={hasOriginalTiles}
          showAddBlockDisclosure={showAddBlockDisclosure}
        />
        <ReportBody
          ref={reportBodyRef}
          reportData={reportData}
          isEditMode={isEditMode}
          onRemoveBlock={handleRemoveBlock}
          onLayoutChange={handleLayoutChange}
          onTileMetaChange={onTileMetaChange}
          isLocalUpdate={isLocalUpdate}
          showAddBlockDisclosure={showAddBlockDisclosure}
          layoutResetKey={layoutResetKey}
          getCurrentMergedData={getCurrentMergedData}
        />
        {isEditMode && (
          <ReportFooter
            ref={reportFooterRef}
            onSave={handleSaveChanges}
            onCancel={handleCancelChanges}
            isSaving={isSaving}
            hasOriginalTiles={hasOriginalTiles}
          />
        )}
        {showAddBlockDisclosure?.isOpen && (
          <AddBlockToReport
            modalDisclosure={showAddBlockDisclosure}
            onAddBlock={handleAddBlocks}
          />
        )}
      </Flex>
    );
  });

ReportContainer.displayName = 'ReportContainer';
